import { ModalV2, TextInput } from "@/components";
import { ValidationPatterns } from "@/utils/form-validation";
import InputErrorMessage from "@/components/input-error-message";
import { StyledSelectBoxContainer } from "@/pages/TDCLIENT/users/add-edit-user-modal/styles";
import { Box, Grid, Typography } from "@mui/material";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useGetAllClientsQuery } from "@/services";
import { MultiCheckbox } from "@/components";
import { useAppDispatch, useAppSelector } from "@/store";
import { roles } from "@/roles";
import { useGetTenantAllowedPagesQuery, useSetPartnerAccessibleClientsMutation, useSetUserAllowedPagesMutation } from "@/graphql/page-access";
import { spOwnerPartnerApi } from "@/services/spowner/partner-api";
 

type AddPartnerModalProps = {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  handleCreatePartner: (e: SPOwnerPartnerTypes) => void;
  btnLoader: boolean;
};

const AddPartnerModal: React.FC<AddPartnerModalProps> = ({
  show,
  setShow,
  handleCreatePartner,
  btnLoader = false,
}) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<SPOwnerPartnerTypes>();

  const user = useAppSelector((s) => s.auth.user);
  const { selectedTenant } = useAppSelector((s) => s.tenantManagement);
  const isTDAdminSP = user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "SERVICE_PROVIDER";
  const { data: clientsResp } = useGetAllClientsQuery({ pageNo: 1, pageSize: 100, searchKeyword: "", ...(isTDAdminSP && { tenantId: selectedTenant?._id }) });
  const clients = clientsResp?.data ?? [];
  const [selectedClientIds, setSelectedClientIds] = React.useState<string[]>([]);
  const { data: tenantPagesResp } = useGetTenantAllowedPagesQuery(
    isTDAdminSP ? { tenantId: selectedTenant?._id } : undefined
  );
  const [allowedPages, setAllowedPages] = React.useState<string[]>([]);
  const PAGE_OPTIONS: Array<{ key: string; label: string }> = [
    { key: "ANALYTICS", label: "Analytics" },
    { key: "POTENTIAL_SAVINGS", label: "Potential Savings" },
    { key: "SCENARIO_MODELING", label: "Scenario Modeling" },
    { key: "CHAT_ASSISTANCE", label: "Chat Assistance" },
    { key: "PRODUCT_HUB", label: "Product Hub" },
    { key: "TRADE_DATA", label: "Trade Data" },
    { key: "DOCUMENTS", label: "Documents" },
    { key: "POST_IMPORT_ADJUSTMENT", label: "Post Import Adjustment" },
  ];
  const availablePageKeys = tenantPagesResp?.pages || [];
  const filteredOptions = PAGE_OPTIONS.filter((o) => availablePageKeys.includes(o.key));
  const [setUserAllowedPages, { isLoading: savingPages }] = useSetUserAllowedPagesMutation();
  const [setPartnerClients, { isLoading: savingClients }] = useSetPartnerAccessibleClientsMutation();
  const dispatch = useAppDispatch();

  const formFields: Array<{
    name: keyof SPOwnerPartnerTypes;
    label: string;
    type: string;
    rules: object;
    placeholder: string;
  }> = [
    {
      name: "firstName",
      label: "First Name",
      type: "text",
      rules: { required: "First name is required" },
      placeholder: "Enter First name",
    },
    {
      name: "lastName",
      label: "Last Name",
      type: "text",
      rules: { required: "Last name is required" },
      placeholder: "Enter Last name",
    },
    {
      name: "email",
      label: "Email Address",
      type: "email",
      placeholder: "hi@example.com",
      rules: {
        required: "Email is required",
        pattern: {
          value: ValidationPatterns.email,
          message: "Invalid email address",
        },
      },
    },
  ];

  return (
    <ModalV2
      show={show}
      setShow={setShow}
      buttonAction={handleSubmit(async (data) => {
        await handleCreatePartner(data);
        // After create, try to find the new partner by email and set their allowed pages and clients
        try {
          const resp = await dispatch(
            spOwnerPartnerApi.endpoints.getAllSpOwnerPartners.initiate({
              page: 1,
              limit: 10,
              searchKeyword: data.email,
              ...(isTDAdminSP && { tenantId: selectedTenant?._id }),
            })
          ).unwrap();
          const newPartner = resp?.data?.find((p) => p.email === data.email);
          if (newPartner?._id) {
            const finalPages = (allowedPages || []).filter((k) => availablePageKeys.includes(k));
            if (finalPages.length > 0) {
              await setUserAllowedPages({ userId: newPartner._id, pages: finalPages }).unwrap();
            }
            if (selectedClientIds.length > 0) {
              await setPartnerClients({ partnerId: newPartner._id, clientIds: selectedClientIds }).unwrap();
            }
          }
        } catch {}
      })}
      isConfirmButtonLoading={btnLoader || savingPages || savingClients}
      buttonText="Invite"
      title="Add New Partner"
      subTitle="They'll receive an email invitation to join Riffs and manage their client accounts"
      modalStyles={{
        "& .MuiPaper-root.MuiPaper-elevation.MuiPaper-rounded": {
          width: "500px",
        },
      }}
    >
      <Box component="form" onSubmit={handleSubmit(handleCreatePartner)}>
        <StyledSelectBoxContainer>
          <Grid container spacing={2}>
            {formFields.map((field) => (
              <Grid
                item
                xs={12}
                sm={field.name === "email" ? 12 : 6}
                key={field.name}
              >
                <Controller
                  name={field.name}
                  control={control}
                  rules={field.rules}
                  render={({ field: { onChange, value } }) => (
                    <TextInput
                      label={field.label}
                      type={field.type}
                      placeholder={field.placeholder}
                      value={value as string}
                      handleChangeValue={onChange}
                      error={!!errors[field.name]}
                    />
                  )}
                />
                <InputErrorMessage message={errors[field.name]?.message} />
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ mb: 1 }} fontWeight={500} fontSize={14} variant="caption">
              Assign Clients (optional)
            </Typography>
            <MultiCheckbox
              label="Clients"
              checkboxes={clients.map((c) => ({ label: c.firstName }))}
              checked={clients.map((c) => selectedClientIds.includes(c._id))}
              handleParentChange={(e) => {
                const checked = e.target.checked;
                if (checked) setSelectedClientIds(clients.map((c) => c._id));
                else setSelectedClientIds([]);
              }}
              handleChildChange={(index) => () => {
                const id = clients[index]?._id;
                if (!id) return;
                setSelectedClientIds((prev) =>
                  prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                );
              }}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ mb: 1 }} fontWeight={500} fontSize={14} variant="caption">
              Pages (optional)
            </Typography>
            <MultiCheckbox
              label="Core Pages"
              checkboxes={filteredOptions.map((o) => ({ label: o.label }))}
              checked={filteredOptions.map((o) => allowedPages.includes(o.key))}
              handleParentChange={(e) => {
                const checked = e.target.checked;
                if (checked) setAllowedPages(filteredOptions.map((o) => o.key));
                else setAllowedPages([]);
              }}
              handleChildChange={(index) => () => {
                const key = filteredOptions[index]?.key;
                if (!key) return;
                setAllowedPages((prev) =>
                  prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
                );
              }}
            />
            {filteredOptions.length === 0 && (
              <Typography sx={{ color: "#8E9494", fontSize: "12px", mt: 0.5 }}>
                No tenant pages configured. Contact your administrator.
              </Typography>
            )}
          </Box>
        </StyledSelectBoxContainer>
      </Box>
    </ModalV2>
  );
};

export default AddPartnerModal;
